import { useState, type Key } from "react";
import {
  useQuery,
  useSuspenseQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Modal, Table, Button, Space } from "antd";
import { useLocation } from "react-router-dom";
import { oldBaseApi } from "@/entities/api";
import { env } from "@/shared/config";
import { useCoordinator } from "@/shared/lib/hooks";
import { fallbackString, formatDate, isUnifiedRoute } from "@/shared/utils";
import { projectQueries } from "@entities/project";
import { DocumentModalTitleCell } from "@features/generic-document-modal";
import { InputSearch } from "@shared/ui";
import {
  DocTypeEnum,
  DocTypeTypes,
  docTypeToEndpointMap,
  docTypeUsesPost,
  docTypeToOldTab,
} from "../../../../model/types/document-types";
import { RenderExpandedRow } from "../../model/render-expanded-row";
import "./section-document-modal.scss";

export interface DocumentItem {
  key: string;
  title: string;
  code: string;
  number: string;
  date: string;
  pages: number;
  originalData: any;
}

interface WorkingDraftResponse {
  entityList: any[];
  pageCount: number;
  pageNumber: number;
  pageSize: number;
}

interface SectionDocumentModalProps {
  visible: boolean;
  onCancel: () => void;
  onConfirm: (selected: DocumentItem[]) => void;
  docType: DocTypeEnum;
}

export const SectionDocumentModal = ({
  visible,
  onCancel,
  onConfirm,
  docType,
}: SectionDocumentModalProps) => {
  const location = useLocation();
  const isUnified = isUnifiedRoute(location.pathname);
  
  const [expandedRowKeys, setExpandedRowKeys] = useState<Key[]>([]);
  const [selectedRowsMap, setSelectedRowsMap] = useState<
    Record<string, DocumentItem>
  >({});
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(isUnified ? 5 : 10);

  const { projectId } = useCoordinator();
  const queryClient = useQueryClient();
  const { data: project } = useSuspenseQuery(
    projectQueries.getProject({ projectId }),
  );

  const { data, isLoading, isPending } = useQuery<WorkingDraftResponse, Error>({
    queryKey: ["docs", docType, search, currentPage, pageSize],
    queryFn: async () => {
      const endpoint = docTypeToEndpointMap[docType];
      const isPost = docTypeUsesPost[docType];

      const commonParams = {
        projectId: project.externalId,
        pageNumber: currentPage - 1,
        pageSize,
        searchValue: search,
        sortField: "creationDate",
        direction: "DESC",
      };

      const response = isPost
        ? await oldBaseApi.post<WorkingDraftResponse>(
            `/filestore/api/v1/${endpoint}/search`,
            commonParams,
          )
        : await oldBaseApi.get<WorkingDraftResponse>(
            `/filestore/api/v1/${endpoint}`,
            {
              params: {
                ...commonParams,
                sortDirection: commonParams.direction,
              },
            },
          );

      return response.data;
    },
    enabled: visible,
  });

  const tableData: DocumentItem[] =
    data?.entityList.map((doc) => ({
      key: `${doc.id}`,
      title: doc.name,
      code: doc.cipher,
      number: doc.number,
      date: doc.issueDate,
      pages: doc.pdfPagesCount,
      originalData: doc,
    })) ?? [];

  const filteredData = tableData.filter((doc) =>
    doc.title.toLowerCase().includes(search.toLowerCase()),
  );

  const isRegulationDoc = docType === DocTypeTypes.REGULATION_DOC;

  const columns = [
    {
      title: "Документ",
      dataIndex: "title",
      key: "title",
      render: (text: string, record: DocumentItem) => (
        <DocumentModalTitleCell
          title={text}
          originalData={record.originalData}
        />
      ),
    },
    ...(isRegulationDoc
      ? [
          {
            title: "Номер документа",
            dataIndex: "number",
            key: "number",
            render: (value: string) => fallbackString(value),
          },
        ]
      : []),
    {
      title: "Шифр",
      dataIndex: "code",
      key: "code",
    },
    {
      title: "Дата выдачи",
      dataIndex: "date",
      key: "date",
      render: (date: string) => fallbackString(formatDate(date)),
    },
    {
      title: "Листы",
      dataIndex: "pages",
      key: "pages",
      render: (pages: number) => <div>{pages}</div>,
    },
  ];

  const rowSelection = {
    selectedRowKeys: Object.keys(selectedRowsMap),
    onSelect: (record: DocumentItem, selected: boolean) => {
      setSelectedRowsMap((prev) => {
        const next = { ...prev };
        const key = String(record.key);
        if (selected) {
          next[key] = record;
        } else {
          delete next[key];
        }
        return next;
      });
    },
    onSelectAll: (
      selected: boolean,
      selectedRows: DocumentItem[],
      changeRows: DocumentItem[],
    ) => {
      setSelectedRowsMap((prev) => {
        const next = { ...prev };

        if (selected) {
          selectedRows.forEach((row) => {
            next[String(row.key)] = row;
          });
        } else {
          changeRows.forEach((row) => {
            delete next[String(row.key)];
          });
        }

        return next;
      });
    },
  };

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      width={1024}
      title="Выберите документ"
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Отменить
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={() =>
            onConfirm(Object.values(selectedRowsMap))
          }
        >
          Подтвердить выбор
        </Button>,
      ]}
    >
      <InputSearch
        placeholder="Поиск по наименованию"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginBottom: 16, width: "25%" }}
      />
      <Space
        style={{
          width: "100%",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        {docType !== DocTypeTypes.REGULATION_DOC &&
        docType !== DocTypeTypes.BUILT_BLUEPRINT ? (
          <Button
            onClick={() => {
              window.open(
                `${env.OLD_FRONTEND_URL}/projects/${project.externalId}/contracts/${project.externalId}/docstore/add-file/?tab=${docTypeToOldTab[docType]}`,
                "_blank",
                "noopener,noreferrer",
              );
            }}
            type="default"
            className="upload-document-btn"
          >
            Загрузить документ
          </Button>
        ) : null}

        {docType !== DocTypeTypes.REGULATION_DOC &&
        docType !== DocTypeTypes.BUILT_BLUEPRINT ? (
          <Button
            onClick={() => {
              queryClient.invalidateQueries({
                queryKey: ["docs", docType, search, currentPage, pageSize],
              });
            }}
            type="link"
          >
            Обновить список
          </Button>
        ) : null}
      </Space>

      <Table
        columns={columns}
        dataSource={filteredData}
        rowSelection={rowSelection}
        pagination={{
          current: currentPage,
          pageSize,
          total: data?.pageCount ? data.pageCount * pageSize : 0,
          showSizeChanger: true,
          pageSizeOptions: ["5", "10", "20", "30", "40", "50", "100"],
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
        }}
        className="sub-documents-table"
        loading={isLoading || isPending}
        expandable={{
          // eslint-disable-next-line react/no-unstable-nested-components
          expandedRowRender: () => <RenderExpandedRow />,
          expandIcon: () => null,
          expandedRowClassName: () => "expand-row-animate",
          expandedRowKeys,
          onExpand: (expanded, record) => {
            setExpandedRowKeys(expanded ? [record.key] : []);
          },
        }}
      />
    </Modal>
  );
};
